import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import CheckerBackground from "../components/CheckerBackground";
import { Button } from "../components/ui/button";
import { Loader2, Flag, Mail, ArrowRight, ArrowLeft, CheckCircle2, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import { InstallPrompt } from "../components/InstallPrompt";

const ICON = "/mytrack-icon.png";

export default function Login() {
  const { requestMagicLink } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await requestMagicLink(email);
      setSent(true);
      if (data.devLink) setDevLink(data.devLink);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls = "w-full rounded-lg border border-mt-border bg-mt-card px-4 py-3 text-sm outline-none focus:border-mt-primary focus:ring-1 focus:ring-mt-primary";

  return (
    <>
    <div className="grid min-h-screen md:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-mt-text md:block">
        <CheckerBackground />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <Flag size={40} className="mb-4 text-mt-accent" />
          <h1 className="font-head text-4xl font-black leading-tight tracking-tighter lg:text-5xl">
            Log every run.<br />Improve every pass.
          </h1>
          <p className="mt-4 max-w-md text-base text-white/80">
            New to MyTrackApp? Start a free 10-day trial — see everything it does before you buy.
          </p>
          <Link to="/" data-testid="login-explore-home" className="mt-6 inline-flex w-fit items-center gap-2 rounded-lg bg-mt-primary px-5 py-3 text-sm font-bold text-white transition-transform hover:scale-[0.98]">
            Explore features & free trial <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      <div className="flex items-center justify-center bg-mt-bg px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <Link to="/" data-testid="login-back-home" className="mb-6 inline-flex items-center gap-1.5 text-sm font-bold text-mt-mutedfg transition-colors hover:text-mt-primary md:hidden">
            <ArrowLeft size={16} /> Back to home
          </Link>
          <Link to="/" data-testid="login-logo-home" className="mb-8 flex items-center gap-3">
            <img src={ICON} alt="MyTrack" className="h-12 w-12 rounded-xl" />
            <span className="font-head text-2xl font-black tracking-tight">MyTrack<span className="text-mt-primary">App</span></span>
          </Link>

          {sent ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="magic-sent">
              <CheckCircle2 size={40} className="text-mt-success" />
              <h2 className="mt-3 font-head text-2xl font-bold">Check your email</h2>
              <p className="mt-1 text-sm text-mt-mutedfg">
                We sent a secure sign-in link to <span className="font-semibold text-mt-text">{email}</span>. It expires in 15 minutes.
              </p>

              {devLink && (
                <a href={devLink} data-testid="dev-link" className="mt-5 flex items-center justify-center gap-2 rounded-lg border border-mt-primary bg-mt-primary/5 px-4 py-3 text-sm font-bold text-mt-primary hover:bg-mt-primary/10">
                  <ExternalLink size={16} /> Open sign-in link (dev)
                </a>
              )}

              <button
                onClick={() => { setSent(false); setDevLink(""); }}
                data-testid="use-different-email"
                className="mt-4 w-full text-center text-sm font-semibold text-mt-primary hover:underline"
              >
                Use a different email
              </button>
            </motion.div>
          ) : (
            <>
              <h2 className="font-head text-2xl font-bold">Sign in</h2>
              <p className="mt-1 text-sm text-mt-mutedfg">Enter your email and we'll send you a magic link — no password needed.</p>

              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="magic-form">
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-mt-mutedfg" />
                  <input className={`${inputCls} pl-9`} type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="email-input" />
                </div>

                {error && <p className="text-sm font-semibold text-mt-text" data-testid="auth-error">{error}</p>}

                <Button type="submit" disabled={loading} data-testid="magic-submit" className="w-full gap-2 bg-mt-primary py-6 text-base font-bold text-white hover:bg-mt-primary/90">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />} Send magic link
                </Button>
              </form>

              <div className="my-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-widest text-mt-mutedfg">
                <span className="h-px flex-1 bg-mt-border" /> or <span className="h-px flex-1 bg-mt-border" />
              </div>

              <button onClick={signInWithGoogle} data-testid="google-signin" className="flex w-full items-center justify-center gap-3 rounded-lg border border-mt-border bg-mt-card py-3 text-sm font-bold text-mt-text transition-colors hover:border-mt-primary">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
                Sign in with Google
              </button>
            </>
          )}
        </motion.div>
      </div>
    </div>
      <InstallPrompt />
    </>
  );
}
